import { useBranding } from "@/contexts/BrandContext";

/**
 * Renders the active brand's configured `footer_links` as a simple footer nav.
 *
 * Reads the links from {@link useBranding}. For the platform-default brand the
 * array is empty (`is_default: true` → `footer_links: []`), so this renders
 * nothing and the page stays pixel-identical to the pre-white-label build. When
 * a dealer brand supplies links they appear as muted external anchors.
 *
 * Links are plain `{label, url}` strings (URLs are gated server-side by the
 * backend `URLValidator`); they are rendered as anchors with
 * `target="_blank" rel="noopener noreferrer"` — never via
 * `dangerouslySetInnerHTML`.
 */
export function BrandFooter() {
  const { footer_links, brand_name } = useBranding();

  if (footer_links.length === 0) {
    return null;
  }

  const year = new Date().getFullYear();

  return (
    <footer className="mt-auto border-t border-gray-100 bg-gray-50/50 px-6 py-6 text-sm text-gray-500">
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 sm:flex-row">
        {brand_name ? (
          <div className="order-2 flex items-center gap-2 sm:order-1">
            {/* Brand-tinted mark gives the copyright line an anchor so the
                footer reads as a deliberate band, not a stray line of text. */}
            <span
              aria-hidden="true"
              className="h-2.5 w-2.5 rounded-full bg-brand-primary"
            />
            <span className="text-gray-500">
              <span className="font-medium text-gray-600">{brand_name}</span> ©{" "}
              {year}
            </span>
          </div>
        ) : (
          // Keep the flex layout balanced (nav pinned right on sm+) even when the
          // brand has no name — an aria-hidden empty placeholder, no visible text.
          <span aria-hidden="true" className="order-2 sm:order-1" />
        )}
        <nav className="order-1 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 sm:order-2 sm:justify-end">
          {footer_links.map((link, index) => (
            <a
              key={index}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-sm font-medium text-gray-500 transition-colors hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/50 focus-visible:ring-offset-1"
            >
              {link.label}
            </a>
          ))}
        </nav>
      </div>
    </footer>
  );
}
